import { Reducer, AnyAction } from 'redux';
import { normalizeSequence } from './utils/normalize-sequence';
import { SavedReport, SavedReportsCommonState } from './types-store';
import { clearValidationForKeys } from './validation';
import {
  ACTION,
  RequestReportEvaluation, ReceiveReportEvaluation, FailReportEvaluation,
  SelectCurrentSavedReports, UpdateCurrentSavedReports, UpdateCurrentValidation,
  RequestAddSavedReports, ReceiveAddSavedReports, FailAddSavedReports,
  UpdateSavedReport, DeleteSavedReport, DeleteAllSavedReports,
  SwitchDisplayDisabled, UpdateSavedReportValidation
} from './action-types';

const reducers: { [indexer: string]: Reducer<SavedReportsCommonState> } = {

  [ACTION.REQUEST_REPORT_EVALUATION]: (state: SavedReportsCommonState,
    action: RequestReportEvaluation): SavedReportsCommonState => {

    if (state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: true
    };
  },

  [ACTION.RECEIVE_REPORT_EVALUATION]: (state: SavedReportsCommonState,
    action: ReceiveReportEvaluation): SavedReportsCommonState => {

    if (!state.fetchingCurrentEvaluation ||
      state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: false,
      currentSavedReport: {
        ...state.currentSavedReport,
        code: action.reportCode,
        selectedReport: action.selectedReport,
        editable: action.editable
      },
      currentValidation: action.validation
    };
  },

  [ACTION.FAIL_REPORT_EVALUATION]: (state: SavedReportsCommonState,
    action: FailReportEvaluation): SavedReportsCommonState => {

    if (!state.fetchingCurrentEvaluation ||
      state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: false
    };
  },

  [ACTION.SELECT_CURRENT_SAVED_REPORT]: (
    state: SavedReportsCommonState,
    action: SelectCurrentSavedReports
  ): SavedReportsCommonState => {

    const currentSavedReportId = action.savedReport?.code?.value;

    return {
      ...state,
      currentSavedReportId,
      currentSavedReport: action.savedReport,
      currentSavedReportOnlyIdMode: action.onlyIdMode,
      currentValidation: state.savedReportsValidation?.[currentSavedReportId]
    };
  },

  [ACTION.UPDATE_CURRENT_SAVED_REPORT]: (
    state: SavedReportsCommonState,
    action: UpdateCurrentSavedReports
  ): SavedReportsCommonState => {

    const currentSavedReport = {
      ...(state.currentSavedReport || {}),
      ...action.savedReport
    } as SavedReport;

    const currentValidation = clearValidationForKeys(state.currentValidation, Object.keys(action.savedReport));

    return {
      ...state,
      currentSavedReport,
      currentValidation
    };
  },

  [ACTION.UPDATE_CURRENT_VALIDATION]: (
    state: SavedReportsCommonState,
    action: UpdateCurrentValidation
  ): SavedReportsCommonState => {

    return {
      ...state,
      currentValidation: action.validation
    };
  },

  [ACTION.REQUEST_ADD_SAVED_REPORTS]: (
    state: SavedReportsCommonState,
    action: RequestAddSavedReports
  ): SavedReportsCommonState => {

    return {
      ...state,
      fetchingAddSavedReports: true
    };
  },

  [ACTION.RECEIVE_ADD_SAVED_REPORTS]: (
    state: SavedReportsCommonState,
    action: ReceiveAddSavedReports
  ): SavedReportsCommonState => {

    return {
      ...state,
      savedReports: normalizeSequence([
        ...(state.savedReports ?? []),
        ...action.savedReports
      ]),
      fetchingAddSavedReports: false
    };
  },

  [ACTION.FAIL_ADD_SAVED_REPORTS]: (
    state: SavedReportsCommonState,
    action: FailAddSavedReports
  ): SavedReportsCommonState => {

    return {
      ...state,
      fetchingAddSavedReports: false
    };
  },

  [ACTION.UPDATE_SAVED_REPORT]: (
    state: SavedReportsCommonState,
    action: UpdateSavedReport
  ): SavedReportsCommonState => {

    const currentSavedReportId = action.currentSavedReportId;
    const stateSavedReports = state.savedReports ?? [];
    const existingSavedReport = stateSavedReports.find(
      report => report.code?.value === currentSavedReportId
    );
    const currentSavedReport = existingSavedReport?.code && existingSavedReport.code.value === action.savedReport.code?.value
      ? {
        ...action.savedReport,
        code: existingSavedReport.code
      }
      : action.savedReport;

    const { [currentSavedReportId]: _, ...filteredValidation } = state.savedReportsValidation ?? {};
    const validation = Object.keys(action.validation || {}).length > 0
      ? {
        ...filteredValidation,
        [currentSavedReport?.code?.value]: action.validation
      }
      : filteredValidation;

    const sequence = currentSavedReport.sequence ?? 0;

    if (existingSavedReport?.sequence === sequence) {
      return {
        ...state,
        savedReports: [
          ...stateSavedReports.filter(report => report.sequence < sequence),
          currentSavedReport,
          ...stateSavedReports.filter(report => report.sequence >= sequence && report !== existingSavedReport)
        ],
        savedReportsValidation: validation
      };
    }

    const filteredSavedReports = existingSavedReport
      ? normalizeSequence(stateSavedReports.filter(report => report !== existingSavedReport))
      : stateSavedReports;

    const savedReports = [
      ...filteredSavedReports
        .filter(report => report.sequence < sequence),
      currentSavedReport,
      ...filteredSavedReports
        .filter(report => report.sequence >= sequence)
        .map(report => ({
          ...report,
          sequence: report.sequence + 1
        }))
    ];

    return {
      ...state,
      savedReports: normalizeSequence(savedReports),
      savedReportsValidation: validation
    };
  },

  [ACTION.DELETE_SAVED_REPORT]: (
    state: SavedReportsCommonState,
    action: DeleteSavedReport
  ): SavedReportsCommonState => {

    const savedReport = state.savedReports?.find(report => report.code?.value === action.savedReportId);

    if (!savedReport) {
      return state;
    }

    const savedReports = state.savedReports
      .filter(report => report !== savedReport);
    const currentSavedReportId = savedReport.code?.value;
    const { [currentSavedReportId]: _, ...validation } = state.savedReportsValidation;

    return {
      ...state,
      savedReports: normalizeSequence(savedReports),
      savedReportsValidation: validation
    };
  },

  [ACTION.DELETE_ALL_SAVED_REPORTS]: (
    state: SavedReportsCommonState,
    action: DeleteAllSavedReports
  ): SavedReportsCommonState => {

    return {
      ...state,
      savedReports: [],
      savedReportsValidation: {}
    };
  },

  [ACTION.SWITCH_DISPLAY_DISABLED]: (
    state: SavedReportsCommonState,
    action: SwitchDisplayDisabled
  ): SavedReportsCommonState => {

    return {
      ...state,
      displayDisabled: action.displayDisabled
    };
  },

  [ACTION.UPDATE_VALIDATION]: (
    state: SavedReportsCommonState,
    action: UpdateSavedReportValidation
  ): SavedReportsCommonState => {

    return {
      ...state,
      savedReportsValidation: action.validation
    };
  }
};

export const withSavedReportsReducer =
  <S extends SavedReportsCommonState>(component: string, reducer: Reducer<S>) =>
    (state: S, action: AnyAction) => {
      const r = action.component === component && reducers[action.type];
      return ((r && r(state as any, action)) || reducer(state, action)) as S;
    };
